// あそびかた：ゲームの仕組みを項目ごとに読める説明書。分類のタブ → 項目の目次 → 本文（枠の中だけスクロール）。
// 中身は help/topics.ts（数値はなるべく core から読むので、調整しても説明が古くならない）。
// 戦闘中はメニューから openHelp('battle') で札として開く（戦闘を離れない）。
import { go, register } from '../router';
import { esc } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { onSwipe } from '../ui/fit';
import { openModal } from '../ui/overlay';
import { HELP_CATS, HELP_TOPICS, type HelpCat, type HelpTopic } from '../help/topics';
import '../styles/screens/help.css';

let cat: HelpCat = 'basic';
let topicId = HELP_TOPICS[0].id;

const topicsOf = (c: HelpCat) => HELP_TOPICS.filter((t) => t.cat === c);
const findTopic = (id: string | null) => HELP_TOPICS.find((t) => t.id === id);

/** 目次と本文。modal は戦闘中の札（分類のタブを出さず、決めた分類の中だけ） */
function helpHtml(c: HelpCat, t: HelpTopic, modal: boolean) {
  const list = topicsOf(c);
  const i = list.indexOf(t);
  const prev = list[i - 1];
  const next = list[i + 1];
  return `${modal ? '' : `<div class="tabs seg-tabs help-cats" role="tablist">${HELP_CATS.map(([k, l, ic]) => `<button class="tab-btn ${k === c ? 'on' : ''}" role="tab" aria-selected="${k === c}" data-cat="${k}">${icon(ic)}${l}</button>`).join('')}</div>`}
    <div class="help-body">
      <nav class="help-toc" aria-label="項目">${list.map((x) => `<button class="${x === t ? 'on' : ''}" data-topic="${x.id}" ${x === t ? 'aria-current="true"' : ''}>${icon(x.icon)}<span>${esc(x.title)}</span></button>`).join('')}</nav>
      <article class="help-art" data-swipe-help>
        <h2 class="help-h">${icon(t.icon)}${esc(t.title)}</h2>
        <div class="help-text">${t.body()}</div>
        <footer class="help-pager">
          ${prev ? `<button class="btn small" data-topic="${prev.id}">${icon('left')}${esc(prev.title)}</button>` : '<span></span>'}
          ${next ? `<button class="btn small" data-topic="${next.id}">${esc(next.title)}${icon('right')}</button>` : '<span></span>'}
        </footer>
      </article>
    </div>`;
}

/** 目次・前後・タブを押したとき、分類と項目を合わせて描き直す。戻り値で外す */
function bind(root: HTMLElement, getCat: () => HelpCat, set: (c: HelpCat, t: HelpTopic) => void) {
  const click = (e: Event) => {
    const el = e.target as HTMLElement;
    const zoom = el.closest<HTMLElement>('[data-zoom]');
    if (zoom) return void zoomShot(zoom);
    const to = el.closest<HTMLElement>('[data-help-go]');
    if (to) return go(to.dataset.helpGo!);
    const cb = el.closest<HTMLElement>('[data-cat]');
    if (cb) {
      const c = cb.dataset.cat as HelpCat;
      return set(c, topicsOf(c)[0]);
    }
    const tb = el.closest<HTMLElement>('[data-topic]');
    const t = tb && findTopic(tb.dataset.topic!);
    if (t) set(t.cat, t);
  };
  root.addEventListener('click', click);
  const off = onSwipe(root, '[data-swipe-help]', (d) => {
    const list = topicsOf(getCat());
    const cur = list.findIndex((x) => x.id === topicId);
    const t = list[cur + d];
    if (t) set(t.cat, t);
  });
  return () => {
    root.removeEventListener('click', click);
    off();
  };
}

/** 写真を大きく（番号札ごと） */
function zoomShot(btn: HTMLElement) {
  const fig = btn.closest('figure');
  openModal(`<div class="hp-zoomed">${btn.innerHTML}</div>`, { title: fig?.querySelector('figcaption')?.textContent ?? '', className: 'help-zoom' });
}

/** 描き直したあと、本文を先頭へ・目次の今の項目を見える所へ */
function settle(root: HTMLElement) {
  root.querySelector('.help-art')?.scrollTo({ top: 0 });
  root.querySelector('.help-toc .on')?.scrollIntoView({ block: 'nearest', inline: 'center' });
}

register('help', {
  title: 'あそびかた',
  mount(el, params) {
    const want = findTopic(params.get('t'));
    if (want) {
      cat = want.cat;
      topicId = want.id;
    }
    el.innerHTML = `<div class="page fill"><section class="panel-paper deco list-page help-page">
      <header class="screen-head"><h1 class="screen-title">${artIcon('guide')}あそびかた</h1></header>
      <div class="help-wrap"></div></section></div>`;
    const wrap = el.querySelector<HTMLElement>('.help-wrap')!;
    const draw = () => {
      const t = findTopic(topicId) ?? topicsOf(cat)[0];
      wrap.innerHTML = helpHtml(cat, t, false);
      settle(wrap);
    };
    draw();
    return bind(
      wrap,
      () => cat,
      (c, t) => {
        cat = c;
        topicId = t.id;
        draw();
      },
    );
  },
});

/** 画面を離れずに読む札（戦闘中のメニューから）。分類の中だけを行き来する */
export function openHelp(c: HelpCat, first?: string) {
  let t = findTopic(first ?? null) ?? topicsOf(c)[0];
  const m = openModal('<div class="help-wrap in-modal"></div>', { title: `あそびかた：${HELP_CATS.find(([k]) => k === c)?.[1] ?? ''}`, wide: true, className: 'help-modal' });
  const wrap = m.body.querySelector<HTMLElement>('.help-wrap')!;
  const draw = () => {
    topicId = t.id;
    wrap.innerHTML = helpHtml(c, t, true);
    settle(wrap);
  };
  draw();
  bind(
    wrap,
    () => c,
    (_c, x) => {
      if (x.cat !== c) return;
      t = x;
      draw();
    },
  );
  return m;
}
